import { Head, usePage } from '@inertiajs/react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import type { SharedProps } from '@/types'
import HeaderLocaleMenu from '@/components/HeaderLocaleMenu'
import { t } from '@/lib/i18n'

/** 居中的认证卡片：登录 / 注册 / 找回密码 共用外壳，自带品牌头与 flash 提示。 */
export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  const { flash } = usePage<SharedProps>().props

  return (
    <div className="relative min-h-screen grid place-items-center px-4 py-10">
      <Head title={title} />

      {/* 右上角，与登录后的顶栏同一个位置——用户在登录前后找的是同一个地方。
          原先放在卡片下方，得先看完整张表单才发现能切语言。 */}
      <div className="absolute top-4 right-4">
        <HeaderLocaleMenu />
      </div>

      <div className="w-full max-w-[400px] grid gap-4">
        <div className="text-center">
          <div className="font-extrabold text-2xl bg-gradient-to-r from-brand to-brand2 bg-clip-text text-transparent">
            📦 HabitChest
          </div>
          <div className="text-muted text-sm mt-1">{t('auth_shell.tagline')}</div>
        </div>

        <section className="bg-card border border-line rounded-2xl card-shadow px-6 py-6">
          <h1 className="text-[17px] font-bold m-0">{title}</h1>
          {subtitle && <p className="text-muted text-sm mt-1 mb-0">{subtitle}</p>}

          {flash?.notice && (
            <div className="mt-4 text-sm rounded-[10px] px-3 py-2 bg-green-tint text-green-strong border border-green-line">
              {flash.notice}
            </div>
          )}
          {flash?.alert && (
            <div className="mt-4 text-sm rounded-[10px] px-3 py-2 bg-red-tint text-red-strong border border-red-line">
              {flash.alert}
            </div>
          )}

          <div className="mt-5 grid gap-4">{children}</div>
        </section>

        {footer && <div className="text-center text-sm text-muted">{footer}</div>}
      </div>
    </div>
  )
}

/** 带标签与错误提示的输入框。 */
export function Field({
  label,
  error,
  ...props
}: { label: string; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="grid gap-1.5">
      <span className="text-sm font-semibold text-ink">{label}</span>
      <input
        {...props}
        className={
          'border rounded-[10px] px-3 py-2.5 outline-none transition-colors ' +
          (error ? 'border-red-line focus:border-red-strong' : 'border-line focus:border-brand')
        }
      />
      {error && <span className="text-red-strong text-[13px]">{error}</span>}
    </label>
  )
}

/** 主按钮，提交中禁用。 */
export function SubmitButton({ children, disabled }: { children: ReactNode; disabled?: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="bg-gradient-to-r from-brand to-brand2 text-white font-bold rounded-[10px] px-4 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95"
    >
      {children}
    </button>
  )
}
